// @ts-check
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "micro-i18n";
import { actions } from "actions";
import { connect } from "unistore/preact";
import { JotType } from "consts";
import { onFocusRef } from "utils";

/**
 * @template T
 * @typedef {import('preact').ComponentType<T>} ComponentType
 */

/**
 * @typedef {import('preact').JSX.Element} JSXElement
 * @typedef {import('preact').RefObject<HTMLElement>} RefObject
 *
 * @typedef {Object} ComplianceRequestFormProps
 * @property {string} id - DOM id prefix used for inputs and labels
 * @property {(agree: boolean | null, deleteData?: boolean) => void} onComplianceAgree - Compliance handler; agree=null means delete-only. TODO(CS-5386): replace with a shared OnComplianceAgree typedef once defined
 * @property {() => void} onClose - Closes the parent menu modal
 * @property {() => void} onCancel - Collapses the form back to the toggle button (does not close the menu modal)
 * @property {RefObject} [focusRef] - Ref attached to the rendered section for focus management
 * @property {(payload: { space: string, type: string, data?: Record<string, unknown> }) => void} jot - Telemetry action injected by connect(). TODO(CS-5386): replace inline payload type with a shared JotPayload typedef in src/types/
 */

/**
 * @param {ComplianceRequestFormProps} props
 * @returns {JSXElement}
 */
const ComplianceRequestForm = ({ id, onComplianceAgree, onClose, onCancel, focusRef, jot }) => {
  const [optOut, setOptOut] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Move focus to the success section when delete-only submission completes,
  // since the previously focused submit button has unmounted
  useEffect(() => {
    isSubmitted && onFocusRef(focusRef);
  }, [isSubmitted]);

  const hasSelection = optOut || deleteData;
  const optOutCheckboxId = `${id}-compliance-opt-out`;
  const deleteDataCheckboxId = `${id}-compliance-delete-data`;

  const handleSubmit = () => {
    // Submit is disabled unless at least one option is selected, so exactly
    // one of these branches matches per submit
    const jotType = optOut && deleteData ? JotType.MenuOoYD : optOut ? JotType.MenuOoY : JotType.MenuOoD;
    jot({ space: "poll", type: jotType });

    if (optOut) {
      // single compliance call: accepted=0 (+ delete=1 if deleteData also checked)
      onComplianceAgree(false, deleteData);
      onClose();
    } else {
      // delete only: compliance call with delete=1, no accepted change
      onComplianceAgree(null, true);
      setIsSubmitted(true);
    }
  };

  if (isSubmitted) {
    return (
      <section ref={focusRef} className="csw-menu-compliance outline" tabIndex={0}>
        <p className="csw-menu-compliance-message">{i18n.t("COMPLIANCE_REQUEST_SUCCESS")}</p>
      </section>
    );
  }

  return (
    <section ref={focusRef} className="csw-menu-compliance outline" tabIndex={0}>
      <fieldset className="csw-compliance-request-fieldset">
        <legend className="sr-only">{i18n.t("COMPLIANCE_REQUEST_LEGEND")}</legend>
        <label className="csw-compliance-request-option" htmlFor={optOutCheckboxId}>
          <input
            id={optOutCheckboxId}
            type="checkbox"
            className="outline"
            checked={optOut}
            onChange={
              /** @param {import('preact').JSX.TargetedEvent<HTMLInputElement>} e */
              (e) => setOptOut(e.currentTarget.checked)
            }
          />
          <span>{i18n.t("COMPLIANCE_REQUEST_OPT_OUT")}</span>
        </label>
        <label className="csw-compliance-request-option" htmlFor={deleteDataCheckboxId}>
          <input
            id={deleteDataCheckboxId}
            type="checkbox"
            className="outline"
            checked={deleteData}
            onChange={
              /** @param {import('preact').JSX.TargetedEvent<HTMLInputElement>} e */
              (e) => setDeleteData(e.currentTarget.checked)
            }
          />
          <span>{i18n.t("COMPLIANCE_REQUEST_DELETE_DATA")}</span>
        </label>
      </fieldset>
      <div className="csw-btns csw-stretch">
        <button type="button" className="csw-btn csw-btn-sm outline" onClick={onCancel}>
          {i18n.t("COMPLIANCE_REQUEST_CANCEL")}
        </button>
        <button type="button" className="csw-btn csw-btn-sm outline" disabled={!hasSelection} onClick={handleSubmit}>
          {i18n.t("COMPLIANCE_REQUEST_SUBMIT")}
        </button>
      </div>
    </section>
  );
};

ComplianceRequestForm.propTypes /* remove-proptypes */ = {
  id: PropTypes.string.isRequired,
  onComplianceAgree: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  focusRef: PropTypes.object,
  jot: PropTypes.func,
};

/** @type {ComponentType<ComplianceRequestFormProps>} */
export default connect("", actions)(ComplianceRequestForm);
